import { plural } from './plural'

/** Strings for a connection's endpoint, workflows and delivery trace (S13). */
export const ingressCopy = {
  retry: 'Retry',
  endpoint: {
    heading: 'Endpoint',
    url: 'URL',
    loadFailed: 'Could not load the endpoint.',
    secretSet: 'Signing secret set',
    noSecret:
      'No signing secret — requests are rejected until you generate one.',
    newSecret: 'New signing secret — shown only once, copy it now.',
    secret: 'Signing secret',
    generate: 'Generate secret',
    replace: 'Replace secret',
    replaceTitle: 'Replace signing secret',
    replaceDescription:
      'Senders still using the current secret will be rejected.',
    replaceConfirm: 'Replace',
    generateFailed: (reason: string) =>
      `Failed to generate a secret — ${reason}`,
    howToSign: 'How to sign a request',
    signatureHeader: 'Signature header',
    signature: 'Signature',
    signatureValue: (prefix: string) =>
      `${prefix} followed by the hex HMAC-SHA256 of the raw body, keyed with the secret`,
    deliveryHeader: 'Delivery id header',
    required: 'required',
    optional: 'optional',
    deliveryNote: 'Retries with the same id are handled once.',
    example: 'curl',
    exampleCaption: 'example',
  },
  workflows: {
    heading: 'Workflows',
    loadFailed: 'Could not load workflows.',
    none: 'No workflow yet — events land in the topic and nothing replies.',
    paused: 'paused',
    fromRecipe: 'from a recipe',
    byAgent: 'set up by the agent',
    everyEvent: 'every event',
    remove: 'Remove',
    removeName: (agent: string) => `Remove the workflow for ${agent}`,
    agent: 'Agent',
    chooseAgent: 'Choose an agent',
    eventType: 'Event type',
    eventTypeHelp: 'A trailing .* matches a family of events.',
    instructions: 'Instructions',
    optional: '(optional)',
    add: 'Add workflow',
    cancel: 'Cancel',
    addFailed: (reason: string) => `Failed to add the workflow — ${reason}`,
    removeFailed: (reason: string) =>
      `Failed to remove the workflow — ${reason}`,
  },
  trace: {
    heading: 'Recent deliveries',
    refresh: 'Refresh',
    loadFailed: 'Could not load deliveries.',
    emptyTitle: 'No deliveries yet',
    emptyDescription:
      'Each request the connection accepts appears here with the routes it matched.',
    rejected: (count: number, time: string, reason: string) =>
      `${plural(count, { one: '{n} request refused', other: '{n} requests refused' })}, most recently ${time} — ${reason}`,
    noType: 'no type',
    topic: (name: string) => `#${name}`,
    topicFallback: (id: number) => `topic ${id}`,
    item: (entity: string) => `item ${entity}`,
    outcome: {
      routed: 'routed',
      sunk: 'fallback',
      unmatched: 'unmatched',
      dropped: 'dropped',
      partial: 'partial',
      failed: 'failed',
      pending: 'pending',
      unknown: 'unknown',
    },
    explain: {
      sunk: 'No other route matched.',
      unmatched: "No route's conditions matched.",
      dropped: 'A drop rule on the connection discarded it.',
      droppedByRule: (n: number) => `Rule ${n} of the connection dropped it.`,
      partial: 'Some routes failed.',
    },
    invocation: {
      failed: 'failed',
      invokedAgents: 'invoked agents',
      invoked: (n: number) =>
        plural(n, { one: 'invoked {n} agent', other: 'invoked {n} agents' }),
      coalesced: 'coalesced',
      capped: 'event cap reached',
      noSubscribers: 'no subscribed agents',
      threadResolved: 'thread marked done',
      alreadyEmitted: 'already delivered',
      threadClosed: 'added to a closed thread',
      pending: 'pending',
      emitted: 'emitted',
    },
  },
} as const
